import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from '@uikit/Form/Button/Button';
import Checkbox from '@uikit/Form/Checkbox/Checkbox';
import Select from '@uikit/Form/Select/Select';
import Loader from '@uikit/old/Loader/Loader';
import BillingFormField from '@components/Publisher/Settings/SettingsBillingDetails/_components/BillingFormField/BillingFormField';
import PayoutFieldRenderer, {
  validatePayoutField,
} from '@components/Publisher/Settings/SettingsBillingDetails/_components/BankAccountForm/PayoutFieldRenderer';

import { getPayoutBankAccountRequirementsApi } from '@api/billing-publisher.api';
import { parseApiErrorMessage } from '@utils/errorUtils';
import {
  PayoutBankAccountTypeType,
  PayoutFieldErrorType,
  PayoutFormFieldType,
} from '@ts/billing-publisher.types';

import {
  ADDRESS_FIELD_KEYS,
  countryCurrencyMap,
  currencyOptions,
  LEGAL_TYPES,
  LegalType,
} from './constants';
import { resolveBillingAddressForRequirement } from './utils';

import './BankAccountForm.scss';

type BillingAddressInput = {
  addressLine1: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
};

export type BankAccountFormValue = {
  currency: string;
  // Wise recipient type (e.g. "iban", "aba", "sort_code"), taken from the
  // selected PayoutBankAccountType.type.
  type: string;
  accountHolderName: string;
  // The Wise `details` object: includes `legalType` and every corridor-specific
  // field (iban, accountNumber, sortCode, address.*, ...). Forwarded straight
  // through to Wise.
  details: Record<string, string>;
};

type Props = {
  initialCountry: string;
  initialCurrency?: string;
  initialLegalType: LegalType;
  initialAccountHolderName: string;
  defaultBillingAddress?: BillingAddressInput;
  onSubmit: (value: BankAccountFormValue) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
  apiError?: string;
  // Per-field validation errors returned by BillingPublisherPayoutSettingsResponse
  // (typically forwarded from Wise). `field` matches the key the form submitted:
  // a top-level Wise field (e.g. "account_holder_name") or a `details` key
  // (e.g. "abartn", "address.state").
  apiFieldErrors?: PayoutFieldErrorType[];
};

// Top-level Wise fields the backend reports under their snake_case names.
// Used to route api field errors to the matching form input.
const API_FIELD_ACCOUNT_HOLDER_NAME = 'account_holder_name';
const API_FIELD_CURRENCY = 'currency';
const API_FIELD_TYPE = 'type';

// Wise field keys are returned verbatim in error responses. When we can't map
// an error back to a visible form control (e.g. the field was already removed
// from the requirements payload, or it's an internal Wise field), we still
// surface the message — but with a humanised label instead of the raw key.
const WISE_FIELD_LABELS: Record<string, string> = {
  abartn: 'Routing number (ABA)',
  accountNumber: 'Account number',
  bankCode: 'Bank code',
  bic: 'BIC / SWIFT',
  bsbCode: 'BSB code',
  cardToken: 'Card token',
  clabe: 'CLABE',
  cnaps: 'CNAPS',
  email: 'Email',
  iban: 'IBAN',
  IFSCcode: 'IFSC code',
  institutionNumber: 'Institution number',
  legalType: 'Account holder type',
  phoneNumber: 'Phone number',
  routingNumber: 'Routing number',
  sortCode: 'Sort code',
  swiftCode: 'SWIFT/BIC code',
  transitNumber: 'Transit number',
  'address.firstLine': 'Address line 1',
  'address.secondLine': 'Address line 2',
  'address.city': 'City',
  'address.state': 'State / Province',
  'address.postCode': 'Postal code',
  'address.country': 'Country',
};

const humanLabelForApiField = (field: string): string => {
  if (WISE_FIELD_LABELS[field]) return WISE_FIELD_LABELS[field];
  // Best-effort fallback: turn snake_case / dotted keys into Sentence case so
  // we never leak raw proto names like `account_holder_phone_number`.
  return field
    .replace(/^address\./, 'Address: ')
    .replace(/[_.]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^(.)/, (m) => m.toUpperCase());
};

// Wise field keys that we render with dedicated top-level controls rather than
// through the dynamic field renderer.
const TOP_LEVEL_FIELD_KEYS = new Set(['legalType', 'accountHolderName']);

// Wise field keys we always send with a hardcoded default and never show in the
// UI. Locking `accountType` to CHECKING covers the publisher payout flow without
// asking users to pick between Checking/Savings on every ACH withdrawal.
const HIDDEN_FIELDS_DEFAULTS: Record<string, string> = {
  accountType: 'CHECKING',
};

const addressDetailsFromBilling = (
  billing: BillingAddressInput
): Record<string, string> => ({
  'address.country': (billing.country || '').toUpperCase(),
  'address.state': billing.stateProvince,
  'address.city': billing.city,
  'address.postCode': billing.postalCode,
  'address.firstLine': billing.addressLine1,
});

function BankAccountForm({
  initialCountry,
  initialCurrency,
  initialLegalType,
  initialAccountHolderName,
  defaultBillingAddress,
  onSubmit,
  onCancel,
  isSubmitting,
  apiError,
  apiFieldErrors,
}: Props): JSX.Element {
  const upperCountry = (initialCountry || '').toUpperCase();
  const [currency, setCurrency] = useState<string>(
    initialCurrency || countryCurrencyMap[upperCountry] || 'USD'
  );
  const [legalType, setLegalType] = useState<LegalType>(initialLegalType);
  const [accountHolderName, setAccountHolderName] = useState<string>(initialAccountHolderName);
  const [details, setDetails] = useState<Record<string, string>>({});
  const [useDefaultBillingAddress, setUseDefaultBillingAddress] = useState<boolean>(
    !!defaultBillingAddress
  );
  const [requirements, setRequirements] = useState<PayoutBankAccountTypeType[]>([]);
  const [selectedTypeKey, setSelectedTypeKey] = useState<string>('');
  const [isLoadingReqs, setIsLoadingReqs] = useState<boolean>(false);
  const [requirementsError, setRequirementsError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  // API errors stick around until the user edits the offending field. The set
  // tracks fields whose latest input invalidates the previous server response,
  // and is cleared whenever a fresh `apiFieldErrors` prop arrives.
  const [dismissedApiFieldKeys, setDismissedApiFieldKeys] = useState<Set<string>>(new Set());

  // Tracks the latest requirements request to avoid stale-response races when the
  // user changes inputs quickly (currency/country/refresh_on_change fields).
  const requestSeqRef = useRef(0);
  // Mirrors the latest `details` state so refetches triggered by useEffect can
  // include user-entered values without listing `details` as an effect dependency
  // (which would refetch on every keystroke).
  const detailsRef = useRef(details);
  detailsRef.current = details;

  // Builds the `partial_details` payload to send to the requirements RPC. Mirrors
  // Wise's `details` object shape: includes `legalType` (which is a Wise `details`
  // field per the proto contract) plus any user-entered bank/address fields.
  // `accountHolderName` and `type` are top-level Wise fields — not in `details`.
  const buildDetailsPayload = useCallback(
    (source: Record<string, string>): Record<string, string> => {
      const payload: Record<string, string> = { ...source };
      delete payload.accountHolderName;
      payload.legalType = legalType;
      Object.assign(payload, HIDDEN_FIELDS_DEFAULTS);
      if (useDefaultBillingAddress && defaultBillingAddress) {
        Object.assign(payload, addressDetailsFromBilling(defaultBillingAddress));
      }
      return payload;
    },
    [legalType, useDefaultBillingAddress, defaultBillingAddress]
  );

  const fetchRequirements = useCallback(
    async (sourceDetails?: Record<string, string>) => {
      if (!currency || !upperCountry) return;
      const seq = requestSeqRef.current + 1;
      requestSeqRef.current = seq;
      setIsLoadingReqs(true);
      setRequirementsError(null);
      try {
        const payload = buildDetailsPayload(sourceDetails ?? detailsRef.current);
        const resp = await getPayoutBankAccountRequirementsApi(
          currency,
          upperCountry,
          payload
        );
        if (requestSeqRef.current !== seq) return;
        setRequirements(resp.accountTypesList);
        setSelectedTypeKey((prev) => {
          if (prev && resp.accountTypesList.some((t) => t.type === prev)) return prev;
          return resp.accountTypesList[0]?.type ?? '';
        });
      } catch (e) {
        if (requestSeqRef.current !== seq) return;
        setRequirementsError(parseApiErrorMessage(e) || 'Could not load bank account fields.');
        setRequirements([]);
        setSelectedTypeKey('');
      } finally {
        if (requestSeqRef.current === seq) setIsLoadingReqs(false);
      }
    },
    [currency, upperCountry, buildDetailsPayload]
  );

  useEffect(() => {
    fetchRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, upperCountry, legalType, useDefaultBillingAddress]);

  const selectedType: PayoutBankAccountTypeType | undefined = useMemo(
    () => requirements.find((t) => t.type === selectedTypeKey),
    [requirements, selectedTypeKey]
  );

  const visibleFields = useMemo(() => {
    if (!selectedType) return { account: [], address: [] };
    const account: PayoutFormFieldType[] = [];
    const address: PayoutFormFieldType[] = [];
    selectedType.fieldsList.forEach((f) => {
      if (TOP_LEVEL_FIELD_KEYS.has(f.key)) return;
      if (HIDDEN_FIELDS_DEFAULTS[f.key] != null) return;
      if (ADDRESS_FIELD_KEYS.has(f.key)) {
        address.push(f);
      } else {
        account.push(f);
      }
    });
    return { account, address };
  }, [selectedType]);

  // When the billing address is reused, Wise still expects coded values for
  // fields with `valuesAllowed` (e.g. "ON" rather than "Ontario" for
  // address.state) and only accepts keys that exist in the requirement. Resolve
  // the stored billing values against the selected requirement; whatever can't
  // be resolved is rendered as a regular Wise field for the user to fill in.
  const billingAddressDetails = useMemo(
    () => (defaultBillingAddress ? addressDetailsFromBilling(defaultBillingAddress) : {}),
    [defaultBillingAddress]
  );

  const billingAddressResolution = useMemo(() => {
    if (!useDefaultBillingAddress || !defaultBillingAddress || !selectedType) return null;
    return resolveBillingAddressForRequirement(billingAddressDetails, selectedType.fieldsList);
  }, [useDefaultBillingAddress, defaultBillingAddress, billingAddressDetails, selectedType]);

  const unresolvedBillingFields = useMemo(
    () => billingAddressResolution?.unresolvedFields ?? [],
    [billingAddressResolution]
  );

  const unresolvedBillingKeys = useMemo(
    () => new Set(unresolvedBillingFields.map((field) => field.key)),
    [unresolvedBillingFields]
  );

  // Address fields whose value comes from the billing profile and therefore
  // neither need user input nor client-side validation.
  const isFilledFromBilling = useCallback(
    (key: string): boolean =>
      useDefaultBillingAddress && ADDRESS_FIELD_KEYS.has(key) && !unresolvedBillingKeys.has(key),
    [useDefaultBillingAddress, unresolvedBillingKeys]
  );

  useEffect(() => {
    setDismissedApiFieldKeys(new Set());
  }, [apiFieldErrors]);

  const apiErrorByField = useMemo(() => {
    const map: Record<string, string> = {};
    apiFieldErrors?.forEach(({ field, message }) => {
      if (field && message) map[field] = message;
    });
    return map;
  }, [apiFieldErrors]);

  const dismissApiFieldError = useCallback((apiKey: string) => {
    setDismissedApiFieldKeys((prev) => {
      if (prev.has(apiKey)) return prev;
      const next = new Set(prev);
      next.add(apiKey);
      return next;
    });
  }, []);

  const apiErrorFor = useCallback(
    (apiKey: string): string | undefined => {
      if (dismissedApiFieldKeys.has(apiKey)) return undefined;
      return apiErrorByField[apiKey];
    },
    [apiErrorByField, dismissedApiFieldKeys]
  );

  // Surface API errors for keys we did not render anywhere (unexpected fields
  // returned by Wise, or address fields hidden behind the billing-address
  // preview) so the user still sees them rather than them being lost.
  const renderedApiKeys = useMemo(() => {
    const keys = new Set<string>([
      API_FIELD_ACCOUNT_HOLDER_NAME,
      API_FIELD_CURRENCY,
      API_FIELD_TYPE,
    ]);
    selectedType?.fieldsList.forEach((f) => {
      if (!isFilledFromBilling(f.key)) keys.add(f.key);
    });
    return keys;
  }, [selectedType, isFilledFromBilling]);

  const unmatchedApiErrors = useMemo(
    () =>
      (apiFieldErrors ?? []).filter(
        ({ field, message }) =>
          !!message && !!field && !renderedApiKeys.has(field) && !dismissedApiFieldKeys.has(field)
      ),
    [apiFieldErrors, renderedApiKeys, dismissedApiFieldKeys]
  );

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!selectedType) return errors;
    selectedType.fieldsList.forEach((field) => {
      if (TOP_LEVEL_FIELD_KEYS.has(field.key)) return;
      if (HIDDEN_FIELDS_DEFAULTS[field.key] != null) return;
      if (isFilledFromBilling(field.key)) return;
      const err = validatePayoutField(field, details[field.key] ?? '');
      if (err) errors[field.key] = err;
    });
    return errors;
  }, [selectedType, details, isFilledFromBilling]);

  const isHolderNameValid = accountHolderName.trim().length > 0;
  const isFormValid =
    !!selectedType && isHolderNameValid && Object.keys(fieldErrors).length === 0;

  const errorFor = (key: string): string | undefined => {
    const apiErr = apiErrorFor(key);
    if (apiErr) return apiErr;
    return showErrors ? fieldErrors[key] : undefined;
  };

  const holderNameError = (() => {
    const apiErr = apiErrorFor(API_FIELD_ACCOUNT_HOLDER_NAME);
    if (apiErr) return apiErr;
    return showErrors && !isHolderNameValid ? 'Account holder name is required' : undefined;
  })();

  const setHolderName = (next: string) => {
    setAccountHolderName(next);
    if (apiErrorByField[API_FIELD_ACCOUNT_HOLDER_NAME]) {
      dismissApiFieldError(API_FIELD_ACCOUNT_HOLDER_NAME);
    }
  };

  const setFieldValue = (field: PayoutFormFieldType, next: string) => {
    setDetails((prev) => ({ ...prev, [field.key]: next }));
    if (apiErrorByField[field.key]) {
      dismissApiFieldError(field.key);
    }
    // Per Wise docs, when a field with refreshRequirementsOnChange=true changes,
    // re-POST the recipient payload so Wise can return updated requirements
    // (e.g. address.country=US reveals the list of states).
    if (field.refreshOnChange) {
      fetchRequirements({ ...detailsRef.current, [field.key]: next });
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!isFormValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    const submitDetails: Record<string, string> = { legalType };
    if (selectedType) {
      selectedType.fieldsList.forEach((field) => {
        if (TOP_LEVEL_FIELD_KEYS.has(field.key)) return;
        if (HIDDEN_FIELDS_DEFAULTS[field.key] != null) {
          submitDetails[field.key] = HIDDEN_FIELDS_DEFAULTS[field.key];
          return;
        }
        if (isFilledFromBilling(field.key)) return;
        const value = details[field.key];
        if (value) submitDetails[field.key] = value.trim();
      });
    }
    if (billingAddressResolution) {
      // Only the address keys the Wise requirement asks for, with coded values
      // (e.g. address.state) already mapped onto Wise's allowed options.
      Object.assign(submitDetails, billingAddressResolution.details);
    }

    await onSubmit({
      currency,
      type: selectedTypeKey,
      accountHolderName: accountHolderName.trim(),
      details: submitDetails,
    });
  };

  const isBusinessAccount = legalType === LEGAL_TYPES.BUSINESS;

  const unresolvedBillingFieldHint = (field: PayoutFormFieldType): string => {
    const raw = (billingAddressDetails[field.key] ?? '').trim();
    const label = field.label.toLowerCase();
    return raw
      ? `"${raw}" from your billing address is not one of the ${label} options Wise accepts. Pick the matching one below.`
      : `Wise also needs ${label} for this payout route. Pick it below.`;
  };

  return (
    <form
      className="bank-account-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      noValidate
    >
      <section className="bank-account-form__section">
        <header className="bank-account-form__section-header">
          <h3 className="bank-account-form__section-title">Account holder</h3>
          <p className="bank-account-form__section-description">
            The person or business who owns the bank account.
          </p>
        </header>
        <div className="bank-account-form__holder-info">
          <span className="bank-account-form__holder-type">
            {isBusinessAccount ? 'Business account' : 'Individual account'}
          </span>
          <span className="bank-account-form__holder-hint">From your billing profile</span>
        </div>
        <BillingFormField
          label="Full legal name"
          placeholder="As it appears on the bank account"
          value={accountHolderName}
          onChange={setHolderName}
          isError={!!holderNameError}
          errorMessage={holderNameError}
        />
      </section>

      <section className="bank-account-form__section">
        <header className="bank-account-form__section-header">
          <h3 className="bank-account-form__section-title">Bank account</h3>
          <p className="bank-account-form__section-description">
            Where we&rsquo;ll send each payout once you withdraw.
          </p>
        </header>

        <BillingFormField
          label="Currency"
          isError={!!apiErrorFor(API_FIELD_CURRENCY)}
          errorMessage={apiErrorFor(API_FIELD_CURRENCY)}
        >
          <Select
            options={currencyOptions}
            value={currencyOptions.find((o) => o.value === currency) || null}
            onChange={(val: { value: string; label: string } | null) => {
              setCurrency(val?.value || '');
              if (apiErrorByField[API_FIELD_CURRENCY]) {
                dismissApiFieldError(API_FIELD_CURRENCY);
              }
            }}
            placeholder="Select destination currency"
            menuPlacement="auto"
          />
        </BillingFormField>

        {requirements.length > 1 && (
          <div
            className="bank-account-form__type-tabs"
            role="tablist"
            aria-label="Payout route"
          >
            {requirements.map((t) => {
              const isActive = t.type === selectedTypeKey;
              return (
                <button
                  key={t.type}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={
                    isActive
                      ? 'bank-account-form__type-tab bank-account-form__type-tab--active'
                      : 'bank-account-form__type-tab'
                  }
                  onClick={() => {
                    setSelectedTypeKey(t.type);
                    if (apiErrorByField[API_FIELD_TYPE]) {
                      dismissApiFieldError(API_FIELD_TYPE);
                    }
                  }}
                >
                  {t.title}
                </button>
              );
            })}
          </div>
        )}

        {!!apiErrorFor(API_FIELD_TYPE) && (
          <div className="bank-account-form__api-error" role="alert">
            {apiErrorFor(API_FIELD_TYPE)}
          </div>
        )}

        {isLoadingReqs && requirements.length === 0 && (
          <div className="bank-account-form__loading">
            <Loader />
          </div>
        )}

        {!isLoadingReqs && !!requirementsError && (
          <div className="bank-account-form__api-error" role="alert">
            {requirementsError}
          </div>
        )}

        {!isLoadingReqs &&
          !requirementsError &&
          requirements.length === 0 &&
          !!currency &&
          !!upperCountry && (
            <div className="bank-account-form__empty">
              No payout routes available for {currency} from {upperCountry}. Try a different
              currency.
            </div>
          )}

        {!isLoadingReqs && !requirementsError && selectedType && (
          <div className="bank-account-form__fields">
            {visibleFields.account.map((field) => (
              <PayoutFieldRenderer
                key={field.key}
                field={field}
                value={details[field.key] ?? ''}
                onChange={(next) => setFieldValue(field, next)}
                error={errorFor(field.key)}
              />
            ))}
          </div>
        )}
      </section>

      {!isLoadingReqs && !!selectedType && visibleFields.address.length > 0 && (
        <section className="bank-account-form__section">
          <header className="bank-account-form__section-header">
            <h3 className="bank-account-form__section-title">Account holder address</h3>
            <p className="bank-account-form__section-description">
              Required by Wise for this currency.
            </p>
          </header>

          {defaultBillingAddress && (
            <label className="bank-account-form__address-toggle">
              <Checkbox
                value={useDefaultBillingAddress}
                onChange={() => setUseDefaultBillingAddress((prev) => !prev)}
              >
                Use my default billing address
              </Checkbox>
            </label>
          )}

          {useDefaultBillingAddress && defaultBillingAddress ? (
            <>
              <div className="bank-account-form__address-preview">
                <div>{defaultBillingAddress.addressLine1}</div>
                <div>
                  {[
                    defaultBillingAddress.city,
                    defaultBillingAddress.stateProvince,
                    defaultBillingAddress.postalCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                <div>{defaultBillingAddress.country?.toUpperCase()}</div>
              </div>
              {unresolvedBillingFields.length > 0 && (
                <div className="bank-account-form__fields">
                  {unresolvedBillingFields.map((field) => (
                    <React.Fragment key={field.key}>
                      <div className="bank-account-form__hint">
                        {unresolvedBillingFieldHint(field)}
                      </div>
                      <PayoutFieldRenderer
                        field={field}
                        value={details[field.key] ?? ''}
                        onChange={(next) => setFieldValue(field, next)}
                        error={errorFor(field.key)}
                      />
                    </React.Fragment>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="bank-account-form__fields">
              {visibleFields.address.map((field) => (
                <PayoutFieldRenderer
                  key={field.key}
                  field={field}
                  value={details[field.key] ?? ''}
                  onChange={(next) => setFieldValue(field, next)}
                  error={errorFor(field.key)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {!!apiError && (
        <div className="bank-account-form__api-error" role="alert">
          {apiError}
        </div>
      )}

      {unmatchedApiErrors.length > 0 && (
        <div className="bank-account-form__api-error" role="alert">
          {unmatchedApiErrors.map((err) => (
            <div key={err.field}>
              <strong>{humanLabelForApiField(err.field)}:</strong> {err.message}
            </div>
          ))}
        </div>
      )}

      <div className="bank-account-form__footer">
        <Button
          className="bank-account-form__submit"
          size="l"
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || isLoadingReqs || !selectedType}
          isLoading={isSubmitting}
        >
          Save bank account
        </Button>
        <Button type="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default BankAccountForm;
